<template>
  <base-screening-page
    page-title="Data Extraction"
    progress-label="Extracted"
    empty-message="All eligible references have been extracted."
    :breadcrumbs="breadcrumbs"
    :references="references"
    :updated-references="updatedReferences"
    :get-reference-log-item-style="getReferenceLogItemStyle"
    :loading="loading"
    :relevant-count="relevantCount"
    :remaining-reference-count="remainingReferenceCount"
    :error-message="error"
    @go-back="goBackToReferences"
  >
    <template #middle-panel="{ selectedReference }">
      <full-text-panel :reference="selectedReference" />
    </template>
    <template #right-panel="{ showingPending, selectedReference }">
      <reference-details-controller
        :review="review"
        :reference="selectedReference"
        :current-researcher="currentResearcher"
        :showing-pending="showingPending"
        :disable-modification="savingReport || !showingPending"
        :tab-order="referenceDetailTabs"
        :has-unsaved-comment.sync="hasUnsavedComment"
        enable-edit-reference-details
        allow-submit-for-consensus
        @save-for-consensus="onSubmitForConsensus"
      />
    </template>
    <template
      v-if="!loading"
      #default="{ selectedReference, selectNextReference }"
    >
      <screening-page-save-modals
        ref="saveController"
        :review="review"
        :reference="selectedReference"
        :current-researcher="currentResearcher"
        :has-unsaved-comment="hasUnsavedComment"
        @save-new-item="onSaveNewItem"
        @select-next-reference="selectNextReference"
      />
    </template>
  </base-screening-page>
</template>

<script>
import BaseScreeningPage from '@/components/Common/BaseScreeningPage';
import FullTextPanel from '@/components/FullTextReviewPage/FullTextPanel';
import { Review, Reference, Researcher } from '@/models';
import ReferenceDetailsController from '@/components/References/ReferenceDetailsController.vue';
import ScreeningPageSaveModals from '@/components/References/ScreeningPageSaveModals.vue';
import { SectionTabs } from '@/components/References/ReferenceDetailsSection.vue';
import {
  GetReviewForDataExtractionPage,
  GetDataExtractionPage,
  GetCurrentResearcher,
  GetReferenceForDataExtraction,
} from '@/graphql';
import { mapActions } from 'vuex';

export default {
  name: 'DataExtractionPage',
  components: {
    BaseScreeningPage,
    FullTextPanel,
    ScreeningPageSaveModals,
    ReferenceDetailsController,
  },
  data: () => ({
    review: null,
    currentResearcher: null,
    topReferencesForDataExtraction: null,
    priorityReference: null,
    updatedItems: [],
    error: null,
    numResolving: 0,
    savingReport: false,
    hasUnsavedComment: false,
  }),
  apollo: {
    review: {
      query: GetReviewForDataExtractionPage,
      variables() {
        return {
          id: this.$route.params.reviewId,
        };
      },
      update: (data) => new Review(data.review),
    },
    currentResearcher: {
      query: GetCurrentResearcher,
      update: (data) => new Researcher(data.currentResearcher),
    },
    topReferencesForDataExtraction: {
      query: GetDataExtractionPage,
      variables() {
        return {
          reviewId: this.$route.params.reviewId,
          count: 20,
        };
      },
      update(data) {
        return data.topReferencesForDataExtraction.map(
          (reference) => new Reference(reference)
        );
      },
      fetchPolicy: 'network-only',
    },
    priorityReference: {
      query: GetReferenceForDataExtraction,
      variables() {
        return {
          id: this.$route.query.show,
        };
      },
      update(data) {
        return new Reference(data.reference);
      },
      skip() {
        return !this.$route.query.show;
      },
    },
  },
  computed: {
    breadcrumbs() {
      const review = this.review ?? {};
      return [
        {
          icon: 'home',
          text: 'Reviews',
          disabled: false,
          href: '/reviews',
        },
        {
          text: review.title,
          href: `/review/${review.id}`,
          disabled: false,
        },
        {
          text: 'Data Extraction',
          href: `/review/${review.id}/data-extraction`,
          disabled: true,
        },
      ];
    },
    loading() {
      return (
        this.review === null ||
        this.references === null ||
        this.currentResearcher === null
      );
    },
    referenceDetailTabs() {
      // Data leads so the panel still opens on the extraction form (Tabs is a
      // `mandatory` item group, so the first tab wins on load).
      return [
        SectionTabs.DATA,
        SectionTabs.INFO,
        SectionTabs.COMMENTS,
        SectionTabs.EVENTS,
      ];
    },
    updatedReferences() {
      return this.updatedItems.map((item) => item.reference);
    },
    references() {
      if (!this.topReferencesForDataExtraction) return null;

      const loadedRefs = [...this.topReferencesForDataExtraction];
      const missingResolvedRefs = this.updatedReferences.filter(
        (x) => !loadedRefs.some((y) => x.id === y.id)
      );
      missingResolvedRefs.sort(Reference.sortByTime);
      loadedRefs.push(...missingResolvedRefs);

      if (this.priorityReference) {
        const priorityRef = this.priorityReference;
        const index = loadedRefs.findIndex(
          (x) => x.id === priorityRef.id
        );
        if (index !== -1) {
          loadedRefs.splice(index, 1);
        }
        loadedRefs.unshift(priorityRef);
      }

      return loadedRefs;
    },
    remainingReferenceCount() {
      const progressSummary = this.review
        ?.dataExtractionProgressSummary;
      if (!progressSummary) {
        return 0;
      }

      return (
        progressSummary.needExtractionCount +
        progressSummary.requiresExtractionByUserCount -
        this.numResolving
      );
    },
    relevantCount() {
      return this.updatedReferences.length;
    },
    currentReviewMember() {
      const reviewers = this.review.reviewMembers;
      const currentResearcher = this.currentResearcher;
      if (!reviewers || !currentResearcher) {
        return null;
      }

      return reviewers.find(
        (reviewer) => reviewer.researcher.id === currentResearcher.id
      );
    },
  },
  methods: {
    ...mapActions({
      createReportForReference:
        'dataExtractionReport/createForReference',
      submitReport: 'dataExtractionReport/submit',
    }),
    getReferenceLogItemStyle(reference) {
      const isUpdated = this.updatedReferences.some(
        (updated) => updated.id === reference.id
      );
      const color = isUpdated ? 'success-light' : 'grey lighten-5';
      return {
        color,
        textClass: 'grey--text text--darken-2',
      };
    },
    goBackToReferences() {
      this.$router.push(`/review/${this.$route.params.reviewId}`);
    },
    onSubmitForConsensus() {
      this.$refs.saveController.submitReport();
    },
    async onSaveNewItem({ reference }) {
      // Optimistically mark the report as complete and select next report.
      let itemToSave;
      try {
        this.savingReport = true;
        itemToSave = await this.createReportIfNotExists(reference);
      } catch (err) {
        this.error =
          'An unexpected error occurred. Please reload the page and try again.';
        return;
      } finally {
        this.savingReport = false;
      }
      try {
        this.numResolving++;
        this.saveItemToCache(itemToSave);

        // Submit the report and update the cache once complete.
        const updatedItem = await this.submitReport({
          report: itemToSave,
        });
        this.saveItemToCache(updatedItem);
      } catch {
        this.removeFromCache(itemToSave);
        this.error =
          'An unexpected error occurred. Please reload the page and try again.';
      } finally {
        this.numResolving--;
      }
    },
    saveItemToCache(item) {
      const index = this.updatedItems.findIndex(
        (x) => x.reference.id === item.reference.id
      );
      if (index >= 0) {
        this.$set(this.updatedItems, index, item);
      } else {
        this.updatedItems.push(item);
      }
    },
    removeFromCache(item) {
      this.updatedItems.splice(this.updatedItems.indexOf(item), 1);
    },
    async createReportIfNotExists(reference) {
      const existing = reference.dataExtractionReports?.find(
        (report) =>
          report.reviewer?.id === this.currentReviewMember.id
      );

      if (existing) {
        return existing;
      }

      return await this.createReportForReference({
        referenceId: reference.id,
        reviewerId: this.currentReviewMember.id,
      });
    },
  },
};
</script>
